<script setup lang="ts">
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import { ref, toRef, computed } from 'vue';
import { getFlagEmoji } from '@/utils/phone-numbers';
import {
  COUNTRY_CODE_BY_SUPPORTED_CURRENCY_CODE,
  CurrencyCode,
} from '@clearbanc/data-common-types';
import { useField } from 'vee-validate';
import { formatMoney } from '@/utils/currency';

interface CurrencyOption {
  label: string;
  code: string;
}

interface Props {
  name?: string;
  currencyOptions?: CurrencyOption[];
  label?: string;
  required?: boolean;
  tooltip?: string;
}

const emit = defineEmits(['change', 'input']);
const props = withDefaults(defineProps<Props>(), {
  name: '',
  currencyOptions: () => [],
  label: '',
  required: false,
  tooltip: '',
});

const { value, errorMessage } = useField<number | null>(toRef(props, 'name'));

const inputAmount = computed<number | null>({
  get: () => value.value ?? null,
  set: (val) => {
    value.value = val;
  },
});

const getFlagSymbol = (currencyCode: CurrencyCode) => {
  if (currencyCode === CurrencyCode.EUR) {
    return '🇪🇺';
  }
  const countryCode =
    COUNTRY_CODE_BY_SUPPORTED_CURRENCY_CODE[currencyCode]?.[0];
  if (!countryCode) {
    return '';
  }
  return getFlagEmoji(countryCode);
};

const selectedCurrency = ref<CurrencyOption>(
  props.currencyOptions[0] || { label: 'US Dollar', code: 'USD' },
);

const placeholderByCurrency = computed(() => {
  return formatMoney('0', selectedCurrency.value.code);
});

const onCurrencyChange = (event: any) => {
  emit('change', { value: event.value.code });
};

const onInput = (event: any) => {
  emit('input', event);
};
</script>
<template>
  <div :class="$style.inputCurrencyGroupContainer">
    <label v-if="props.label" :for="props.name" :class="$style.label">
      {{ props.label }}
      <button
        v-if="props.tooltip"
        v-tooltip.focus="{ value: props.tooltip, escape: true }"
        class="pi pi-info-circle"
        :class="$style.tooltipIcon"
        type="button"
      ></button>
      <span v-if="props.required" :class="$style.required">*</span>
    </label>
    <div
      :class="[$style.inputGroup, { [$style.inputGroupInvalid]: errorMessage }]"
    >
      <div
        v-if="props.currencyOptions.length === 0"
        class="inputGroupAddonLeft"
        :class="[$style.inputGroupAddon]"
      >
        $
      </div>
      <Dropdown
        v-else
        v-model="selectedCurrency"
        data-cy="pay-out-currency"
        :options="props.currencyOptions"
        dataKey="code"
        option-label="label"
        class="inputGroupAddonLeft"
        :class="[$style.currencyDropdown]"
        @change="onCurrencyChange"
      >
        <template #value="slotProps">
          <span :class="$style.currencyFlag">{{
            getFlagSymbol(slotProps.value?.code)
          }}</span>
          <span :class="$style.currencyCode">{{ slotProps.value?.code }}</span>
        </template>

        <template #option="slotProps">
          <div :aria-label="slotProps.option.code">
            <span :class="$style.currencyFlag">{{
              getFlagSymbol(slotProps.option.code)
            }}</span>
            <span :class="$style.currencyCode">{{
              slotProps.option.label
            }}</span>
          </div>
        </template>
      </Dropdown>
      <InputNumber
        v-model="inputAmount"
        :id="props.name"
        :min="0"
        :class="[$style.numberInputField]"
        mode="currency"
        :currency="selectedCurrency?.code"
        :placeholder="placeholderByCurrency"
        @input="onInput"
      />
    </div>
    <div v-if="errorMessage" :class="$style.errorMessage">
      {{ errorMessage }}
    </div>
  </div>
</template>

<style lang="less" scoped>
.inputGroupAddonLeft {
  border-left: 1px solid #d1d5db;
  border-radius: 6px 0 0 6px;
}
</style>

<style lang="less" module>
@error-color: #f0a9a7;

.inputGroup {
  display: flex;
  height: 50px;

  input {
    border-radius: 0 6px 6px 0;
  }

  &.inputGroupInvalid {
    box-shadow: 0px 0 0 1px @error-color;
    border-radius: 6px;
    .currencyDropdown {
      border-color: @error-color;
      &:hover,
      &:focus {
        box-shadow: none;
        outline: none;
        border-color: @error-color;
      }
      &:global(.p-focus) {
        box-shadow: none;
        outline: none;
        border-color: @error-color;
      }
    }
    .inputGroupAddon {
      border-color: @error-color;
    }
    .numberInputField {
      input {
        border-color: @error-color;
        border-left-color: #d1d5db;
        &:hover,
        &:focus {
          box-shadow: none;
          outline: none;
        }
      }
    }
  }
}

.inputCurrencyGroupContainer {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;

  .currencyDropdown {
    background-color: #f8f8fa;
    border-right: none !important;

    &:hover {
      background-color: #e5e7eb;
    }

    &:focus {
      background-color: #e5e7eb;
    }

    :global(.p-inputtext) {
      padding-right: 0;
    }
  }
}

.label {
  font-size: 14px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
}

.tooltipIcon {
  font-size: 14px;
  color: @color-black;
  cursor: help;
  display: inline-flex;
  align-items: center;
}

.required {
  color: #ef4444;
}

.errorMessage {
  color: #bd3e3e;
  font-size: 0.875rem;
  line-height: 20px;
}

.inputGroupAddon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 12px;
  min-width: 41px;
  background-color: #f3f4f6;
  color: #6b7280;
  font-size: 14px;
  border-top: 1px solid #d1d5db;
  border-bottom: 1px solid #d1d5db;

  i {
    font-size: 14px;
  }
}

.currencyFlag {
  font-size: 18px;
  margin-right: 6px;
  font-weight: 500;
}

.currencyCode {
  font-weight: 500;
}
</style>
